import React, { useState, useEffect } from 'react'
import { useLocation, Link } from 'react-router-dom';
import useApi from '../../helpers/TerserahApi';
import { isValidInternationalPhone } from '../../helpers/PhoneUtils';
import { doLogin } from '../../helpers/AuthHandler';
import { PageArea } from './style';
import { PageContainer, PageTitle, ErrorMessage, CountryCodeSelector } from '../../components';
import { useTranslation } from 'react-i18next';

const Page = () => {
  const api = useApi();
  const location = useLocation();

  const [name, setName] = useState('');
  const [stateLoc, setStateLoc] = useState('');
  const [email, setEmail] = useState('');
  const [countryCode, setCountryCode] = useState('+62'); // Indonesia por defecto
  const [phoneNumber, setPhoneNumber] = useState('');
  const [password, setPassword] = useState('');
  const [password_confirmation, setPassword_confirmation] = useState('');
  const [userType, setUserType] = useState('particular'); // Default to particular
  const [stateList, setStateList] = useState([]);
  const [disabled, setDisabled] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [agreeTerms, setAgreeTerms] = useState(false);
  const { t } = useTranslation();
  
  // Bot protection fields
  const [honeypot, setHoneypot] = useState(''); // Hidden field for bots
  const [formStartTime] = useState(Date.now()); // Track form submission speed
  // eslint-disable-next-line no-unused-vars
  const [recaptchaToken, setRecaptchaToken] = useState(null);
  
  // Phone verification state
  const [showPhoneVerification, setShowPhoneVerification] = useState(false);
  const [verificationCode, setVerificationCode] = useState('');
  const [registeredEmail, setRegisteredEmail] = useState('');
  const [registeredPhone, setRegisteredPhone] = useState('');

  useEffect(() => {
    const getStates = async () => {
      const stateList = await api.getStates();
      setStateList(stateList);
    };
    getStates();

    // Load reCAPTCHA v3 script
    const loadRecaptcha = () => {
      const siteKey = process.env.REACT_APP_RECAPTCHA_SITE_KEY;
      if (!siteKey) {
        console.warn('reCAPTCHA site key not configured');
        return;
      }
      
      // Check if script already exists
      if (document.getElementById('recaptcha-script')) {
        return;
      }
      
      const script = document.createElement('script');
      script.id = 'recaptcha-script';
      script.src = `https://www.google.com/recaptcha/api.js?render=${siteKey}`;
      script.async = true;
      script.defer = true;
      document.head.appendChild(script);
    };
    loadRecaptcha();

    // Check for error in URL params
    const params = new URLSearchParams(location.search);
    const errorParam = params.get('error');
    if (errorParam === 'auth_failed') {
      setError('Google authentication failed. Please try again.');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSubmit = async e => {
    e.preventDefault();
    setError('');

    if (!agreeTerms) {
      setError(t('agree_terms_privacy'));
      return;
    }

    if (name && stateLoc && phoneNumber && password && password_confirmation) {
      if (password !== password_confirmation) {
        setError('As senhas não coincidem');
        return;
      };

      // Combinar código de país con número
      const fullPhone = countryCode + phoneNumber;

      // Validar formato del teléfono internacional
      if (!isValidInternationalPhone(fullPhone)) {
        setError(t('phone_format_error'));
        return;
      };

      setDisabled(true);
      
      // Generate reCAPTCHA token
      let captchaToken = null;
      const siteKey = process.env.REACT_APP_RECAPTCHA_SITE_KEY;
      if (siteKey && window.grecaptcha) {
        try {
          captchaToken = await window.grecaptcha.execute(siteKey, { action: 'signup' });
        } catch (error) {
          console.error('reCAPTCHA error:', error);
          // Continue without token if reCAPTCHA fails
        }
      }
      
      const json = await api.register(
        name, 
        stateLoc, 
        email, 
        fullPhone, 
        password, 
        password_confirmation, 
        userType,
        honeypot,
        formStartTime,
        captchaToken
      );

      if (json.error) {
        const jsonErrors = [];
        for(let err in json.error){
          jsonErrors.push(json.error[err].msg);
        };

        setError(jsonErrors.join(', '));
      } else if (json.requiresPhoneVerification) {
        // Mostrar formulario de verificación de teléfono
        setShowPhoneVerification(true);
        setRegisteredEmail(email);
        setRegisteredPhone(fullPhone);
        setSuccess(t('phoneVerificationCodeSent'));
        setError('');
      } else if (json.message) {
        setError(t('registration_success'));
        window.location.href = '/';
        return;
      } else {
        doLogin(
          json.token ?? json.accessToken,
          false,
          json.refreshToken ?? json.refresh_token
        );
        window.location.href = '/';
        return;
      };
    } else {
      setError(t('fill_all_required_fields'))
    };

    setDisabled(false);
  };

  const handleGoogleSignup = () => {
    const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:3001/api';
    // Ensure /api prefix is present
    const baseUrl = apiUrl.endsWith('/api') ? apiUrl : `${apiUrl}/api`;
    window.location.href = `${baseUrl}/auth/google`;
  };

  const handleAppleSignup = () => {
    const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:3001/api';
    const baseUrl = apiUrl.endsWith('/api') ? apiUrl : `${apiUrl}/api`;
    window.location.href = `${baseUrl}/auth/apple`;
  };

  const handleVerifyPhone = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    if (!verificationCode || verificationCode.length !== 6) {
      setError(t('enterVerificationCode'));
      return;
    }

    setDisabled(true);
    try {
      const json = await api.verifyPhoneCode(registeredEmail, verificationCode);
      
      if (json.error) {
        setError(json.error);
      } else if (json.success) {
        setSuccess(t('phoneVerifiedSuccess'));
        // Redirigir al login después de 2 segundos
        setTimeout(() => {
          window.location.href = '/signin';
        }, 2000);
      }
    } catch (err) {
      setError(t('error'));
    }
    setDisabled(false);
  };

  const handleResendCode = async () => {
    setError('');
    setSuccess('');
    setDisabled(true);

    try {
      const json = await api.resendPhoneCode(registeredEmail);
      
      if (json.error) {
        setError(json.error);
      } else if (json.success) {
        setSuccess(t('verificationCodeResent'));
      }
    } catch (err) {
      setError(t('error'));
    }
    setDisabled(false);
  };

  // Si estamos en el paso de verificación de teléfono
  if (showPhoneVerification) {
    return (
      <PageContainer>
        <PageTitle>{t('verifyPhone')}</PageTitle>
        <PageArea>
          {error && <ErrorMessage>{error}</ErrorMessage>}
          {success && <div style={{ color: 'green', marginBottom: '15px', padding: '10px', background: '#e8f5e9', borderRadius: '5px' }}>{success}</div>}
          
          <div className="signup-container">
            <div style={{ textAlign: 'center', marginBottom: '20px' }}>
              <p>{t('phoneVerificationSentTo')}</p>
              <strong>{registeredPhone}</strong>
            </div>
            
            <form onSubmit={handleVerifyPhone}>
              <label className="area">
                <div className="area--title">{t('verificationCode')}:</div>
                <div className="area--input">
                  <input
                    type="text"
                    disabled={disabled}
                    value={verificationCode}
                    onChange={e => setVerificationCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                    placeholder="123456"
                    maxLength="6"
                    style={{ textAlign: 'center', fontSize: '24px', letterSpacing: '8px' }}
                    autoFocus
                  />
                </div>
              </label>
              
              <label className="area">
                <div className="area--title"></div>
                <div className="area--input">
                  <button disabled={disabled}>{t('verify')}</button>
                </div>
              </label>
            </form>
            
            <div style={{ textAlign: 'center', marginTop: '20px' }}>
              <p>{t('didntReceiveCode')}</p>
              <button 
                onClick={handleResendCode} 
                disabled={disabled}
                style={{ 
                  background: 'transparent', 
                  border: 'none', 
                  color: '#667eea', 
                  cursor: 'pointer',
                  textDecoration: 'underline'
                }}
              >
                {t('resendCode')}
              </button>
            </div>
          </div>
        </PageArea>
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <PageTitle>{t('register')}</PageTitle>
      <PageArea>
        {error && <ErrorMessage>{error}</ErrorMessage>}
        <div className="signup-container">
          <form onSubmit={handleSubmit}>
          {/* Honeypot field - hidden from users, bots will fill it */}
          <input
            type="text"
            name="website"
            value={honeypot}
            onChange={e => setHoneypot(e.target.value)}
            style={{ position: 'absolute', left: '-9999px', width: '1px', height: '1px' }}
            tabIndex="-1"
            autoComplete="off"
            aria-hidden="true"
          />
          <label className="area">
            <div className="area--title">{t('name')}:</div>
            <div className="area--input">
              <input
                autoFocus
                type="text"
                disabled={disabled}
                value={name}
                onChange={e => setName(e.target.value)}
              />
            </div>
          </label>
          <label className="area">
            <div className="area--title">{t('state')}:</div>
            <div className="area--input">
              <select
                value={stateLoc}
                onChange={e => setStateLoc(e.target.value)}
                disabled={disabled}
              >
                <option></option>
                {stateList.map((item, key) => (
                  <option value={item.uf} key={key}>{item.name}</option>
                ))}
              </select>
            </div>
          </label>
          <label className="area">
            <div className="area--title">{t('phone')}:*</div>
            <div className="area--input" style={{ display: 'flex', alignItems: 'center' }}>
              <CountryCodeSelector
                selectedCode={countryCode}
                onCodeSelect={setCountryCode}
                disabled={disabled}
                required
              />
              <input
                type="tel"
                disabled={disabled}
                value={phoneNumber}
                onChange={e => setPhoneNumber(e.target.value)}
                placeholder="8123456789"
                required
                style={{ marginLeft: '10px', flex: 1 }}
              />
            </div>
          </label>
          <label className="area">
            <div className="area--title">E-mail ({t('optional')}):</div>
            <div className="area--input">
              <input
                type="email"
                disabled={disabled}
                value={email}
                onChange={e => setEmail(e.target.value)}
              />
            </div>
          </label>
          <label className="area">
            <div className="area--title">{t('passwrd')}:</div>
            <div className="area--input">
              <input
                type="password"
                disabled={disabled}
                value={password}
                onChange={e => setPassword(e.target.value)}
              />
            </div>
          </label>
          <label className="area">
            <div className="area--title">{t('confirm_password')}:</div>
            <div className="area--input">
              <input
                type="password"
                disabled={disabled}
                value={password_confirmation}
                onChange={e => setPassword_confirmation(e.target.value)}
              />
            </div>
          </label>
          <label className="area">
            <div className="area--title">{t('user_type')}:</div>
            <div className="area--input">
              <select
                value={userType}
                onChange={e => setUserType(e.target.value)}
                disabled={disabled}
              >
                <option value="particular">{t('particular')}</option>
                <option value="professional">{t('professional')}</option>
              </select>
            </div>
          </label>
          <label className="area" style={{ marginTop: '10px', marginBottom: '10px' }}>
            <div className="area--title"></div>
            <div className="area--input" style={{ display: 'flex', alignItems: 'flex-start', gap: '10px' }}>
              <input
                type="checkbox"
                checked={agreeTerms}
                onChange={(e) => setAgreeTerms(e.target.checked)}
                disabled={disabled}
                required
                style={{ marginTop: '4px', cursor: 'pointer', width: '18px', height: '18px' }}
              />
              <span style={{ fontSize: '14px', lineHeight: '1.5', color: '#333' }}>
                {t('agree_terms_privacy')}
                {' ('}
                <Link to="/terms" target="_blank" style={{ color: '#667eea', textDecoration: 'none' }}>
                  {t('termsAndConditions')}
                </Link>
                {' & '}
                <Link to="/privacy" target="_blank" style={{ color: '#667eea', textDecoration: 'none' }}>
                  {t('privacyPolicy')}
                </Link>
                {')'}
              </span>
            </div>
          </label>
          <label className="area">
            <div className="area--title"></div>
            <div className="area--input">
              <button disabled={disabled}>{t('sign_up')}</button>
            </div>
          </label>
          </form>
          
          <div className="divider"></div>
          
          <div className="oauth-signin">
            <div className="oauth-buttons">
            <button onClick={handleGoogleSignup} disabled={disabled} className="oauth-btn google-btn">
            <svg viewBox="0 0 24 24" width="48" height="48" xmlns="http://www.w3.org/2000/svg">
              <g transform="matrix(1, 0, 0, 1, 27.009001, -39.238998)">
                <path fill="#4285F4" d="M -3.264 51.509 C -3.264 50.719 -3.334 49.969 -3.454 49.239 L -14.754 49.239 L -14.754 53.749 L -8.284 53.749 C -8.574 55.229 -9.424 56.479 -10.684 57.329 L -10.684 60.329 L -6.824 60.329 C -4.564 58.239 -3.264 55.159 -3.264 51.509 Z"/>
                <path fill="#34A853" d="M -14.754 63.239 C -11.514 63.239 -8.804 62.159 -6.824 60.329 L -10.684 57.329 C -11.764 58.049 -13.134 58.489 -14.754 58.489 C -17.884 58.489 -20.534 56.379 -21.484 53.529 L -25.464 53.529 L -25.464 56.619 C -23.494 60.539 -19.444 63.239 -14.754 63.239 Z"/>
                <path fill="#FBBC05" d="M -21.484 53.529 C -21.734 52.809 -21.864 52.039 -21.864 51.239 C -21.864 50.439 -21.724 49.669 -21.484 48.949 L -21.484 45.859 L -25.464 45.859 C -26.284 47.479 -26.754 49.299 -26.754 51.239 C -26.754 53.179 -26.284 54.999 -25.464 56.619 L -21.484 53.529 Z"/>
                <path fill="#EA4335" d="M -14.754 43.989 C -12.984 43.989 -11.404 44.599 -10.154 45.789 L -6.734 42.369 C -8.804 40.429 -11.514 39.239 -14.754 39.239 C -19.444 39.239 -23.494 41.939 -25.464 45.859 L -21.484 48.949 C -20.534 46.099 -17.884 43.989 -14.754 43.989 Z"/>
              </g>
            </svg>
            {t('sign_up_with_google')}
            </button>
            
            <button onClick={handleAppleSignup} disabled={disabled} className="oauth-btn apple-btn">
              <svg width="48" height="48" viewBox="0 0 24 24" fill="currentColor">
                <path d="M17.05 20.28c-.98.95-2.05.8-3.08.35-1.09-.46-2.09-.48-3.24 0-1.44.62-2.2.44-3.06-.35C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.54 4.09l.01-.01zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z"/>
              </svg>
              Sign up with Apple
            </button>
            </div>
          </div>
        </div>
      </PageArea>
    </PageContainer>
  );
};

export default Page;